import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Delta, EmptyState, HalalBadge, LiveBadge } from "@/components/common";
import { MarketsTabs } from "@/components/markets/MarketsTabs";
import { TradeSheet } from "@/components/order/TradeSheet";
import { NewsFeed } from "@/components/NewsFeed";
import { Sparkline } from "@/components/Charts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtCcy, fmtUSD, timeAgo } from "@/lib/format";
import { Search, Star, Clock, Plus, Globe } from "lucide-react";
import { toast } from "sonner";
import { toastError } from "@/lib/toastError";

const PAGE = 60;

const REGION_META = {
  eu: { title: "European Stocks", blurb: "Trade listed companies across Europe's exchanges" },
  asia: { title: "Asian Stocks", blurb: "Trade listed companies across Asia-Pacific exchanges" },
  world: { title: "World Stocks", blurb: "Everywhere else — the Americas, Middle East, Africa and Oceania" },
};

export default function WorldMarkets({ region = "world" }) {
  const meta = REGION_META[region] || REGION_META.world;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [continent, setContinent] = useState(null);
  const [market, setMarket] = useState(null);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [coverage, setCoverage] = useState("simulated");
  const [srcType, setSrcType] = useState("simulated");
  const [loadingList, setLoadingList] = useState(false);
  const [idx, setIdx] = useState(null);
  const [search, setSearch] = useState("");
  const [localOnly, setLocalOnly] = useState(true);
  const [country, setCountry] = useState(null);
  const [allTotal, setAllTotal] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ticket, setTicket] = useState({ open: false, symbol: null });

  const currentGroup = groups.find((g) => g.continent === continent);
  const currentExchange = currentGroup?.exchanges.find((e) => e.code === market);
  const marketNameOf = (code) => {
    for (const g of groups) { const e = g.exchanges.find((x) => x.code === code); if (e) return e.name; }
    return code;
  };

  const loadExchanges = useCallback(async () => {
    const { data } = await api.get("/market/exchanges", { params: { region } });
    setGroups(data);
    const wanted = searchParams.get("continent");
    const startG = data.find((g) => g.continent === wanted) || data[0];
    if (startG) {
      setContinent(startG.continent);
      setMarket(startG.exchanges[0]?.code || null);
    }
    // `searchParams` is read once on mount to seed the initial continent; the
    // dedicated effect below handles later ?continent= changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);
  // Real listings (Twelve Data, cached) with server-side search + pagination.
  // The response is tagged with the exchange it belongs to, so a slow reply for one market
  // can never land in the list of another.
  const listedFor = useRef(null);
  const loadListings = useCallback(async (m, q, skip = 0, local = true) => {
    if (!m) return;
    listedFor.current = m;
    setLoadingList(true);
    try {
      const { data } = await api.get(`/market/listings/${m}`, {
        params: { q: q || undefined, limit: PAGE, skip, local: local || undefined },
      });
      if (listedFor.current !== m) return;
      setSrcType(data.source); setCoverage(data.coverage); setTotal(data.total);
      setCountry(data.country || null); setAllTotal(data.all_total ?? null);
      setList((prev) => (skip ? [...prev, ...data.data] : data.data));
    } catch (e) {
      if (listedFor.current !== m) return;
      setList([]); setTotal(0); toastError("Couldn't load listings", e, { id: "world-listings" });
    } finally { if (listedFor.current === m) setLoadingList(false); }
  }, []);
  const loadIndex = useCallback(async (m) => { if (!m) return; try { const { data } = await api.get(`/market/index/${m}`); setIdx(data); } catch (e) { console.error(e); } }, []);
  const loadSide = useCallback(async () => {
    const [wl, ord] = await Promise.all([api.get("/watchlist"), api.get("/orders?source=global")]);
    setWatchlist(wl.data); setOrders(ord.data);
  }, []);

  useEffect(() => { loadExchanges(); loadSide(); }, [loadExchanges, loadSide]);

  // React to continent changes coming from the nav submenu (?continent=)
  useEffect(() => {
    const wanted = searchParams.get("continent");
    if (wanted && groups.length && wanted !== continent) {
      const g = groups.find((x) => x.continent === wanted);
      if (g) { setContinent(wanted); setMarket(g.exchanges[0]?.code || null); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, groups]);

  // Load index when the exchange changes
  useEffect(() => { if (market) loadIndex(market); }, [market, loadIndex]);

  // A different exchange means a different list — drop the old names straight away.
  useEffect(() => { setList([]); setTotal(0); setCoverage("simulated"); setSrcType("simulated"); }, [market]);

  // Debounced server-side search within the selected exchange's full directory
  useEffect(() => {
    if (!market) return;
    const q = search.trim();
    const h = setTimeout(() => loadListings(market, q, 0, localOnly), q ? 300 : 0);
    return () => clearTimeout(h);
  }, [market, search, localOnly, loadListings]);

  // First visit to an exchange downloads its directory in the background — check back.
  useEffect(() => {
    if (coverage !== "syncing" || !market) return;
    const h = setTimeout(() => loadListings(market, search.trim(), 0, localOnly), 4000);
    return () => clearTimeout(h);
  }, [coverage, market, search, localOnly, loadListings]);

  const onContinentChange = (c) => {
    setContinent(c);
    const g = groups.find((x) => x.continent === c);
    setMarket(g?.exchanges[0]?.code || null);
    setSearch("");
    setSearchParams(c ? { continent: c } : {});
  };

  const addWatch = async (symbol) => { try { await api.post("/watchlist", { symbol }); toast.success(`${symbol} added to watchlist`); loadSide(); } catch (e) { toastError("Couldn't add to watchlist", e); } };

  const isSearching = !!search.trim();
  const display = list;
  const isLive = srcType === "twelve_data";

  const totalExchanges = groups.reduce((a, g) => a + g.exchanges.length, 0);

  return (
    <AppShell>
      <PageHeader title={meta.title}
        subtitle={`${meta.blurb} · ${totalExchanges} exchange${totalExchanges === 1 ? "" : "s"} · ${groups.map((g) => g.continent).join(" · ")}`}
        action={<LiveBadge />} testid="world-header" />

      <MarketsTabs />

      {groups.length > 1 && (
        <Tabs value={continent || ""} onValueChange={onContinentChange} className="mb-3" data-testid="world-continent-tabs">
          <TabsList className="flex-wrap h-auto">{groups.map((g) => <TabsTrigger key={g.continent} value={g.continent} data-testid={`world-continent-${g.continent}`}>{g.continent}</TabsTrigger>)}</TabsList>
        </Tabs>
      )}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <Select value={market || ""} onValueChange={setMarket}>
          <SelectTrigger className="w-[320px] max-w-full" data-testid="world-exchange-select">
            <SelectValue placeholder="Select an exchange" />
          </SelectTrigger>
          <SelectContent className="max-h-[360px]">
            {(currentGroup?.exchanges || []).map((e) => (
              <SelectItem key={e.code} value={e.code} data-testid={`world-exchange-opt-${e.code}`}>
                <span className="flex items-center gap-2">
                  <span className={`h-1.5 w-1.5 rounded-full ${e.is_open ? "bg-emerald-500" : "bg-slate-400"}`} />
                  {e.name} <span className="text-muted-foreground">· {e.currency} · {e.instruments} listed</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentExchange && (
          <Badge variant={currentExchange.is_open ? "default" : "secondary"} data-testid="world-exchange-status">
            {currentExchange.is_open ? "Open" : "Closed"}
          </Badge>
        )}
      </div>

      {idx?.index && (
        <Card className="p-4 sm:p-5 shadow-premium card-premium mb-4 sm:mb-5" data-testid="world-index-header">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{idx.index.name} <span className="opacity-60">({idx.index.code})</span></div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-2xl sm:text-3xl font-semibold numeric">{idx.index.value.toLocaleString()}</span>
                  <Delta value={idx.index.change_pct} />
                  <span className={`numeric text-sm ${idx.index.change_points >= 0 ? "text-emerald-600" : "text-red-600"}`}>{idx.index.change_points >= 0 ? "+" : ""}{idx.index.change_points} pts</span>
                </div>
              </div>
              <div className="hidden md:block w-40 lg:w-56"><Sparkline data={idx.index.spark} color={idx.index.change_pct >= 0 ? "#86A230" : "#C2413A"} height={44} /></div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${idx.status.is_open ? "text-emerald-700" : "text-muted-foreground"}`}
                style={{ background: idx.status.is_open ? "rgba(23,185,120,0.14)" : "rgba(100,116,139,0.12)" }}>
                <span className={`h-2 w-2 rounded-full ${idx.status.is_open ? "bg-emerald-500 live-dot" : "bg-slate-400"}`} />
                {idx.status.is_open ? "Open" : "Closed"}
              </span>
              <div className="text-xs text-muted-foreground leading-tight">
                <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{idx.status.hours} · {idx.status.days_label}</div>
                <div>{idx.status.local_time} {idx.status.tz_label} · {idx.status.next_event}</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4 sm:space-y-5">
          <Card className="p-4 shadow-premium">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-sm font-medium">{marketNameOf(market)} — Listed Companies</div>
              {isLive
                ? <Badge className="text-[10px]" data-testid="world-coverage-badge">Live listings · {total.toLocaleString()}</Badge>
                : coverage === "syncing"
                  ? <Badge variant="secondary" className="text-[10px]" data-testid="world-coverage-badge">Fetching {marketNameOf(market)}'s directory…</Badge>
                  : <Badge variant="secondary" className="text-[10px]" data-testid="world-coverage-badge">{coverage === "plan_unavailable" ? "Not available on current data plan · showing sample" : `Sample · ${total.toLocaleString()}`}</Badge>}
            </div>
            {isLive && (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-secondary/40 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium">
                    {country ? `${country} companies only` : "Local companies only"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {localOnly
                      ? `Hiding foreign companies cross-listed here${allTotal ? ` · ${(allTotal - total).toLocaleString()} hidden` : ""}`
                      : "Showing every line on this exchange, including foreign cross-listings"}
                  </div>
                </div>
                <Switch checked={localOnly} onCheckedChange={setLocalOnly}
                  data-testid="world-local-only-toggle" />
              </div>
            )}
            <div className="relative mb-3">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={`Search all ${total ? total.toLocaleString() + " " : ""}companies on ${marketNameOf(market)}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-9" data-testid="world-search-input" />
            </div>
            {isSearching && (
              <div className="mb-2 text-xs text-muted-foreground" data-testid="world-search-status">
                {loadingList ? "Searching..." : `${total.toLocaleString()} match${total === 1 ? "" : "es"} on ${marketNameOf(market)}`}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2 max-h-[460px] overflow-auto" data-testid="world-price-cards">
              {display.map((s) => (
                <div key={`wm-${s.symbol}`} className="rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer group"
                  onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                  <div className="flex items-start justify-between">
                    <div><div className="font-medium flex items-center gap-2">{s.symbol.split(".")[0]} {s.halal && <HalalBadge />}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[160px]">{s.name}</div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); addWatch(s.symbol); }} data-testid={`world-watch-${s.symbol}`}><Star className="h-4 w-4" /></Button>
                  </div>
                  <div className="flex items-end justify-between mt-2">
                    {s.price != null
                      ? <div><span className="numeric text-lg font-semibold">{fmtCcy(s.price, s.currency)}</span><div className="numeric text-[11px] text-muted-foreground">≈ {fmtUSD(s.price_usd)}</div></div>
                      : <div className="text-[11px] text-muted-foreground">{s.currency || ""} · live price on open</div>}
                    {s.change_pct != null && <Delta value={s.change_pct} className="text-sm" />}
                  </div>
                  <Button size="sm" className="w-full mt-2" onClick={(e) => { e.stopPropagation(); setTicket({ open: true, symbol: s.symbol }); }} data-testid={`world-trade-${s.symbol}`}>Trade</Button>
                </div>
              ))}
              {!display.length && !loadingList && (
                <div className="sm:col-span-2">
                  <EmptyState
                    title={isSearching ? "No matches" : coverage === "syncing" ? `Loading ${marketNameOf(market)}` : "No listings"}
                    hint={isSearching ? "Try another company or ticker"
                      : coverage === "syncing" ? "We're pulling this exchange's full company list for the first time — a few seconds."
                      : "Pick an exchange"} />
                </div>
              )}
            </div>
            {display.length < total && (
              <Button variant="outline" className="w-full mt-3" disabled={loadingList}
                onClick={() => loadListings(market, search.trim(), display.length, localOnly)} data-testid="world-load-more">
                {loadingList ? "Loading..." : `Load more (${(total - display.length).toLocaleString()} more)`}
              </Button>
            )}
          </Card>

          <Card className="p-4 shadow-premium">
            <div className="text-sm font-medium mb-3">Global Order History</div>
            {orders.length ? (
              <Table data-testid="world-orders-table"><TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead>Side</TableHead><TableHead className="text-end">Qty</TableHead><TableHead className="text-end">Price</TableHead><TableHead className="text-end">Time</TableHead></TableRow></TableHeader>
                <TableBody>{orders.slice(0, 10).map((o) => (<TableRow key={o.id}>
                  <TableCell className="font-medium">{o.symbol.split(".")[0]}</TableCell>
                  <TableCell><Badge variant={o.side === "buy" ? "default" : "destructive"} className="capitalize">{o.side}</Badge></TableCell>
                  <TableCell className="text-end numeric">{o.qty}</TableCell>
                  <TableCell className="text-end numeric">{fmtCcy(o.fill_price, o.currency)}</TableCell>
                  <TableCell className="text-end text-xs text-muted-foreground">{timeAgo(o.timestamp)}</TableCell>
                </TableRow>))}</TableBody></Table>
            ) : <EmptyState title="No orders yet" hint="Your executed global trades appear here" />}
          </Card>
        </div>

        <div className="space-y-4 sm:space-y-5">
          <NewsFeed market={market} title={`${currentExchange?.name || "Global"} News`} limit={18} height="360px" />
          <Card className="p-4 shadow-premium" data-testid="world-watchlist">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Star className="h-4 w-4 text-[color:var(--tharwy-lime)]" />Watchlist</div>
            {watchlist.length ? (
              <div className="space-y-2">{watchlist.map((s) => (
                <div key={`wmwl-${s.symbol}`} className="flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-accent transition-colors cursor-pointer"
                  onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                  <div><div className="font-medium text-sm">{s.symbol.split(".")[0]}</div><div className="text-xs text-muted-foreground truncate max-w-[120px]">{s.name}</div></div>
                  <div className="text-end"><div className="numeric text-sm">{fmtCcy(s.price, s.currency)}</div><Delta value={s.change_pct} className="text-xs" /></div>
                </div>
              ))}</div>
            ) : <EmptyState title="Empty watchlist" hint="Add companies from the list" />}
          </Card>
        </div>
      </div>

      <TradeSheet open={ticket.open} onOpenChange={(v) => setTicket({ ...ticket, open: v })} symbol={ticket.symbol} source="global" onDone={loadSide} />
    </AppShell>
  );
}
